import React from 'react';
import { ScriptService } from '../../../../../controllers/ScriptService';
import i18n from '../../../../../i18n';
import { SvgRecording } from '../../../../../icons';
import { Program } from '../../../../../interfaces';
import { StandaloneRecordButton } from '../../../../../views/ats-details/record-modal/standalone-record-button';
import { CastButton } from '../../../../../chromecast/cast-button';
import PlayerControlButton from '../PlayerControlButton';
import './index.scss';
import { classes } from '../../../../../utils/commonUtils';

export interface PlayerControl {
  icon: string;
  onClick: () => void;
  position: 'start' | 'end';
  isDisabled?: boolean;
  className?: string;
}

type Props = {
  isPlaying: boolean;
  isFullscreen: boolean;
  isWatchingLive: boolean;
  volumeControl: JSX.Element;
  audioAndSubtitlesMenu?: JSX.Element;
  streamQualityMenu?: JSX.Element;
  currentProgram?: Program;
  controls?: PlayerControl[];
  hide?: boolean;
  playPause: () => void;
  fastForward: () => void;
  rewind: () => void;
  fullscreen?: () => void;
  airplay?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  playNextEpisode?: () => void;
  togglePictureInPicture?: () => void;
};

class PlayerControlRow extends React.Component<Props> {
  get pictureInPicture() {
    if (this.props.togglePictureInPicture && ScriptService.isPiPSupported()) {
      return (
        <PlayerControlButton
          tooltip={i18n.t<string>('picture in picture')}
          icon="Ï"
          className="pic-in-pic"
          onClickCallback={this.props.togglePictureInPicture}
        />
      );
    }
    return null;
  }

  get chromeCast() {
    if (ScriptService.canCast()) {
      return <CastButton />;
    }
    return null;
  }

  get airPlay() {
    if (ScriptService._isSafari() && this.props.airplay) {
      return <PlayerControlButton icon="G" className="airplay" onClickCallback={this.props.airplay} />;
    }
    return null;
  }

  get playNextEpisode() {
    if (this.props.playNextEpisode) {
      return <PlayerControlButton icon={'Ą'} className="nextEpisode" onClickCallback={this.props.playNextEpisode} />;
    }
    return null;
  }

  render() {
    const {
      volumeControl,
      streamQualityMenu,
      audioAndSubtitlesMenu,
      rewind,
      playPause,
      fastForward,
      fullscreen,
      isPlaying,
      isFullscreen,
      isWatchingLive,
      currentProgram,
    } = this.props;

    let playPauseIcon = isPlaying ? '=' : 'Ă';
    let fullscreenIcon = isFullscreen ? 'f' : 'F';
    let skipForwardClass = isWatchingLive ? ' live' : '';

    const startControls = this.props.controls?.filter((control) => control.position === 'start');
    const endControls = this.props.controls?.filter((control) => control.position === 'end');

    return (
      <div className={classes('player-controls-container', this.props.hide && 'hide')}>
        <div className="player-controls">
          <div className="left-content">
            <PlayerControlButton icon="«" className="rewind" onClickCallback={rewind} />
            <PlayerControlButton icon={playPauseIcon} className="play" onClickCallback={playPause} />
            <PlayerControlButton
              icon="»"
              className={'fast-forward' + skipForwardClass}
              onClickCallback={fastForward}
              disabled={isWatchingLive}
            />

            {currentProgram !== undefined && (
              <StandaloneRecordButton programId={currentProgram.id}>
                {({ hasRecording, open }) => (
                  <PlayerControlButton
                    onClickCallback={open}
                    icon={
                      <SvgRecording className={hasRecording ? 'recording-active' : ''} width="24px" height="24px" />
                    }
                  />
                )}
              </StandaloneRecordButton>
            )}

            {this.playNextEpisode}

            {startControls?.map((control) => (
              <PlayerControlButton
                key={control.icon}
                icon={control.icon}
                className={control.className}
                onClickCallback={control.onClick}
                disabled={control.isDisabled}
              />
            ))}
          </div>
          <div className="right-content">
            {this.airPlay}
            {this.chromeCast}
            <div className="hover-menus" onMouseEnter={this.props.onMouseEnter} onMouseLeave={this.props.onMouseLeave}>
              {volumeControl}
              {audioAndSubtitlesMenu}
              {streamQualityMenu}
              {this.pictureInPicture}
            </div>
            <PlayerControlButton icon={fullscreenIcon} className={'fullscreen'} onClickCallback={fullscreen} />
            {endControls?.map((control) => (
              <PlayerControlButton
                key={control.icon}
                icon={control.icon}
                className={control.className}
                onClickCallback={control.onClick}
                disabled={control.isDisabled}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }
}
export default PlayerControlRow;
